const STOPWORDS = new Set(['the', 'a', 'an', 'and', 'or', 'is', 'to', 'of']);
const EMAIL = /^\S+@\S+\.\S+$/;
const PHONE = /^\+?[\d\s\-()]{7,}$/;
const URL_WITH_PATH = /^https?:\/\/[^/]+\/\S+/i;
const URL_DOMAIN_ONLY = /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}\/?$/i;
const PATH_START = /^\/\S+/;
const UTM_FRAGMENT = /utm_(source|medium|campaign|term|content)=/i;
const POSTAL_US = /^\d{5}(-\d{4})?$/;
const PARTIAL_SKU = /^\d{3,}([-][\dA-Z]+)*$/i;

// Display order of the result groups (and of arrow-key travel through them).
// Sources not listed here follow in fan-out order.
export const GROUP_ORDER = ['goto', 'customers', 'contacts', 'products', 'reps', 'quotes', 'stores', 'interactions', 'interaction_keyvalues', 'campaigns', 'content_images', 'audiences', 'reports', 'forecast_similarity'];
// Numbers (SKUs, customer numbers, quote numbers) — the sources that hold them.
const NUMBER_SOURCES = ['products', 'customers', 'quotes', 'forecast_similarity'];
function compileSkuPatterns(skuFormat) {
  if (!skuFormat || !skuFormat.length) return [];
  return skuFormat.map(p => {
    try {
      return new RegExp(p);
    } catch {
      return null;
    }
  }).filter(Boolean);
}
export function classify(rawTerm, options = {}) {
  const term = (rawTerm || '').trim();
  if (!term) return {
    term: '',
    entities: [],
    hard: false,
    reason: 'empty'
  };
  if (term.length < 2) return {
    term,
    entities: [],
    hard: false,
    reason: 'too_short'
  };
  if (STOPWORDS.has(term.toLowerCase())) return {
    term,
    entities: [],
    hard: false,
    reason: 'stopword'
  };
  const account = options.account || {};
  const currentPath = options.currentPath || '';
  const skuPatterns = compileSkuPatterns(account.skuFormat);
  if (EMAIL.test(term)) return hard(term, ['contacts', 'customers'], 'email');
  if (skuPatterns.some(r => r.test(term))) {
    return hard(term, NUMBER_SOURCES.slice(), 'sku_tenant');
  }

  // People search doesn't match phone numbers yet; a long digit run is as
  // likely a customer, quote or SKU number, so those sources ride along.
  if (PHONE.test(term) && term.replace(/\D/g, '').length >= 7) return hard(term, ['contacts', 'customers', 'quotes', 'products'], 'phone');
  if (URL_WITH_PATH.test(term) || PATH_START.test(term)) return hard(term, ['pages'], 'url_path');
  if (UTM_FRAGMENT.test(term)) return hard(term, ['activity_utm'], 'utm');
  // A 5-digit term is a zip (customers match city/state, not zip — the
  // number sources catch it when it's really a customer/quote/SKU number).
  if (POSTAL_US.test(term)) return hard(term, ['customers', 'products', 'quotes'], 'postal');

  // Two-letter state/country codes ("CA") fall through to the plain-text
  // fan-out: customers match on state, and "co" is as likely "Contacts".

  if (URL_DOMAIN_ONLY.test(term) && !URL_WITH_PATH.test(term)) {
    return hard(term, ['company_analysis'], 'domain');
  }
  if (PARTIAL_SKU.test(term)) return hard(term, NUMBER_SOURCES.slice(), 'number');
  const entities = defaultFanout(account, currentPath);
  const reordered = applyCurrentViewBoost(entities, currentPath);
  const context = extractActivityContext(currentPath) || {};
  return {
    term,
    entities: reordered,
    hard: false,
    reason: 'fallback',
    context
  };
}
function hard(term, entities, reason) {
  return {
    term,
    entities,
    hard: true,
    reason,
    context: {}
  };
}

// Plain text. Section/feature gating happens in service.js (access.js), so a
// source listed here is still skipped when the member can't open it.
function defaultFanout(account, currentPath) {
  const set = ['goto', 'customers', 'contacts', 'products', 'reps', 'quotes', 'stores', 'interactions', 'campaigns', 'content_images', 'audiences', 'reports', 'forecast_similarity'];
  const path = currentPath || '';
  if (path.startsWith('/Research/Company')) set.push('company_analysis');
  if (path.startsWith('/Research/Trends')) set.push('market_trends');
  return set;
}
export function extractActivityContext(currentPath) {
  if (!currentPath) return null;
  const m = currentPath.match(/^\/Activity\/(Detail|KeyValues)\/([^/]+)\/([^/]+)/);
  if (!m) return null;
  return {
    activityType: m[2],
    activityName: m[3]
  };
}
function applyCurrentViewBoost(entities, currentPath) {
  const map = {
    '/Contacts': 'contacts',
    '/Product': 'products',
    '/Audience': 'audiences',
    '/Research/Company': 'company_analysis',
    '/Research/Trends': 'market_trends',
    '/Campaign': 'campaigns',
    '/Campaigns': 'campaigns',
    '/Activity/Pages': 'pages',
    '/Forecast/Product': 'forecast_product',
    '/Forecast/Similarity': 'forecast_similarity'
  };
  const boosted = Object.keys(map).find(p => currentPath.startsWith(p));
  if (!boosted) return entities;
  const entity = map[boosted];
  if (!entities.includes(entity)) entities.unshift(entity);else {
    const idx = entities.indexOf(entity);
    entities.splice(idx, 1);
    entities.unshift(entity);
  }
  return entities;
}
export default classify;